import React from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// Phase D (Feb 2026): "Best Sales of the Day" 🥇🥈🥉 ranking card.
// Combines salesmen + manager direct sales, sorted by boxes desc.
const RANK_BADGE = ["🥇", "🥈", "🥉"];

export default function BestOfDay({ salesmen = [], managers = [] }) {
  const nav = useNavigate();
  const { t, lang } = useLang();

  // Combine — managers tagged
  const all = [
    ...salesmen.map(s => ({ id: s.id, name: s.name, name_ta: s.name_ta, role: "salesman", units_sold: s.units || s.units_sold || 0, total_orders: s.orders || s.total_orders || 0, delivered: s.delivered })),
    ...managers.filter(m => (m.units_sold || 0) > 0).map(m => ({ id: m.id, name: m.name, name_ta: m.name_ta, role: "manager", units_sold: m.units_sold || 0, total_orders: m.total_orders || 0, delivered: m.delivered || 0 })),
  ];
  all.sort((a, b) => (b.units_sold || 0) - (a.units_sold || 0));
  const top = all.slice(0, 3);
  const dispName = (p) => (lang === "ta" && p.name_ta) ? p.name_ta : p.name;

  return (
    <div className="bg-gradient-to-br from-[#0A1A3F] via-[#0055FF] to-[#5856D6] text-white rounded-3xl p-6 shadow-xl" data-testid="mg-best-of-day">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-6 h-6 text-yellow-300" />
          <div className="heading-font font-bold text-xl">{t("best_sales_today")}</div>
        </div>
        <button
          data-testid="mg-best-view-all"
          onClick={() => nav("/management/performance")}
          className="text-xs font-bold bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 flex items-center gap-1 transition"
        >
          {lang === "ta" ? "எல்லாம் பாரு" : "View All"} <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {top.length === 0 ? (
        <div className="text-center py-6 text-white/70">
          {lang === "ta" ? "இன்னும் விற்பனை இல்ல" : "No sales yet today"}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {top.map((p, i) => (
            <div key={`${p.role}-${p.id}`} className="bg-white/10 backdrop-blur-md rounded-2xl p-4 hover:bg-white/15 transition" data-testid={`mg-best-rank-${i+1}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-3xl">{RANK_BADGE[i]}</span>
                <span className={`pill text-[10px] ${p.role === "manager" ? "bg-yellow-300/20 text-yellow-100" : "bg-white/20 text-white"}`}>
                  {p.role === "manager" ? (lang === "ta" ? "மேனேஜர்" : "Manager") : (lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman")}
                </span>
              </div>
              <div className="font-bold heading-font truncate text-base mt-1.5">{dispName(p)}</div>
              <div className="heading-font text-3xl font-bold mt-1.5">
                {p.units_sold || 0}
                <span className="text-xs font-medium opacity-70 ml-1.5">{t("boxes")}</span>
              </div>
              <div className="text-[11px] opacity-70 mt-1">
                {p.delivered != null ? `${p.delivered} ${t("delivered_today").toLowerCase()} • ` : ""}{p.total_orders || 0} {t("orders").toLowerCase()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
